import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Sidebar from "./Sidebar";
import Header from "./Header";
import CommandPalette from "./CommandPalette";
import OnboardingWizard from "./OnboardingWizard";
import SupportWidget from "./SupportWidget";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const SIDEBAR_STORAGE_KEY = "career-os-sidebar-collapsed";

export default function Layout() {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // Close the mobile drawer on Escape (dialog UX). Only bound while open.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  useEffect(() => {
    if (!user) return;
    const skipped = localStorage.getItem("career-os-onboarding-done");
    if (skipped) return;
    api.get("/profile").then((r) => {
      const p = r.data || {};
      const complete = !!(p.name && p.current_role && p.current_salary && (p.skills || []).length);
      if (!complete) setShowOnboarding(true);
    }).catch(() => {});
  }, [user]);

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Desktop sidebar */}
      <div className="hidden md:flex"><Sidebar collapsed={collapsed} setCollapsed={setCollapsed} /></div>

      {/* Mobile sidebar drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/70 md:hidden"
              onClick={() => setMobileOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              data-testid="mobile-sidebar-overlay"
              aria-hidden="true"
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-y-0 left-0 z-50 md:hidden"
              role="dialog"
              aria-modal="true"
              aria-label="Main navigation"
              data-testid="mobile-sidebar-drawer"
            >
              <Sidebar
                collapsed={false}
                setCollapsed={() => setMobileOpen(false)}
                variant="mobile"
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <Header
          onOpenCommand={() => setCmdOpen(true)}
          onOpenSidebarMobile={() => setMobileOpen(true)}
        />
        <main className="flex-1 min-w-0" data-clarity-mask="true">
          <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8 py-6 md:py-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={window.location.pathname}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>

      <CommandPalette open={cmdOpen} setOpen={setCmdOpen} />
      <SupportWidget />
      {showOnboarding && (
        <OnboardingWizard
          onClose={() => setShowOnboarding(false)}
          onDone={() => {
            localStorage.setItem("career-os-onboarding-done", "1");
            setShowOnboarding(false);
          }}
        />
      )}
    </div>
  );
}
